import React, { useCallback, useState } from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ListItem from '@mui/material/ListItem';
import Cropper from 'react-easy-crop';
import Box from '@mui/material/Box';
import useSuspenseFetch from '../../hooks/useSuspenseFetch';
import { usePutMutation } from '../../hooks/useAuthenticatedMutation';

const EditProfileDialog = ({ onClose }) => {
    const { data: user } = useSuspenseFetch('user');
    const putUserMutation = usePutMutation('user');
    const [crop, setCrop] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [imgSrc, setImgSrc] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [showAlert, setShowAlert] = useState(null);

    const onCropComplete = useCallback(
        (croppedArea, croppedAreaPixels) => {
            const canvas = document.createElement('canvas');
            const image = new Image();
            image.src = imgSrc;

            const imageFinalSize = 128;
            canvas.width = imageFinalSize;
            canvas.height = imageFinalSize;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(
                image,
                croppedAreaPixels.x,
                croppedAreaPixels.y,
                croppedAreaPixels.width,
                croppedAreaPixels.height,
                0,
                0,
                imageFinalSize,
                imageFinalSize
            );

            setCroppedImage(canvas.toDataURL('image/jpeg'));
        },
        [imgSrc]
    );

    const handleDeselectFile = () => {
        setImgSrc(null);
        setCroppedImage(null);
        setShowAlert(null);
    };

    const handleSelectFile = e => {
        if (e.target.files && e.target.files.length > 0) {
            setShowAlert({ type: 'info', message: 'Select the desired area by dragging the image with your mouse and zooming it with your mouse wheel' });
            const selectedFile = e.target.files[0]; // Gives the user ability to select the same file multiple times without re-opening this dialog
            e.target.value = '';

            if (selectedFile.type === 'image/jpeg' || selectedFile.type === 'image/png') {
                setCrop({ x: 0, y: 0 });
                setZoom(1);
                const reader = new FileReader();
                reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
                reader.readAsDataURL(selectedFile);
            }
            else {
                handleDeselectFile();
                setShowAlert({ type: 'warning', message: 'Wrong file format selected; only JPEG and PNG images are supported' });
            }
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();

        const params = {
            ...(croppedImage && { avatarBase64: croppedImage.split(';base64,')[1] })
        };

        putUserMutation.mutate(params, { onSuccess: onClose });
    };

    return (
        <Dialog open={true}>
            <DialogTitle>Update your Avatar</DialogTitle>
            <DialogContent>
                {showAlert && <Alert severity={showAlert.type} sx={{ mb: 2 }}>{showAlert.message}</Alert>}
                {imgSrc && (
                    <>
                        {imgSrc && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    mb: 1
                                }}>
                                <Button
                                    disabled={putUserMutation.isPending}
                                    variant="contained"
                                    component="label"
                                    size="small"
                                    sx={{ mb: 1 }}
                                >
                                    Update Avatar
                                    <input type="file" accept="image/*" hidden onChange={e => handleSelectFile(e)} />
                                </Button>
                                <Button
                                    disabled={putUserMutation.isPending}
                                    variant="outlined"
                                    onClick={handleDeselectFile}
                                    component="label"
                                    size="small"
                                    sx={{ mb: 1, ml: 1 }}
                                >
                                    Remove
                                </Button>
                            </Box>
                        )}
                        <ListItem sx={{ height: '300px', mb: 2 }}>
                            <Cropper
                                cropShape="round"
                                zoomSpeed={0.5}
                                image={imgSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </ListItem>
                    </>
                )}
                {!imgSrc && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                        <Avatar src={croppedImage || user.avatarURL} sx={{ width: 150, height: 150, mb: 1 }}>
                            {user.name.substring(0, 1)}
                        </Avatar>
                        <Button
                            disabled={putUserMutation.isPending}
                            variant="contained"
                            component="label"
                            size="small"
                            sx={{ mt: 1 }}
                        >
                            Update Avatar
                            <input type="file" accept="image/*" hidden onChange={e => handleSelectFile(e)} />
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    loading={putUserMutation.isPending}
                    variant="contained"
                    disabled={!croppedImage}
                    onClick={handleSubmit}
                    loadingPosition="start"
                >
                    Save
                </Button>
                <Button disabled={putUserMutation.isPending} variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditProfileDialog;
